import { useTranslation } from 'react-i18next';
import { BuildingSkeleton } from '@components/BuildingSkeleton/BuildingSkeleton';
import classes from './dashboardSkeleton.module.css';

/**
 * Dashboard / Command-Center loading skeleton.
 *
 * Mirrors the live grid one-to-one so the loaded → loading swap is a
 * silent fade rather than a layout reflow:
 *   Row 1 — Visual Site Overview (half-tall) + Workforce Status (half-tall)
 *   Row 2 — Project Progress (full-short)
 *   Row 3 — Master Schedule (full)
 *   Row 4 — Activities Feed (third) + Safety & Compliance (third) + Delivery (third)
 *   Row 5 — Weather Impact (full)
 *   Row 6 — AI Agent (full)
 *
 * Block widths are deliberately jagged — uniform shimmer reads as a
 * generic placeholder, varied widths read as "real content is loading".
 * A `--wave` custom property staggers the shimmer animation per card so
 * the page pulses top-down instead of all-at-once.
 */
export function DashboardSkeleton() {
  const { t } = useTranslation();
  return (
    <div className={classes.skeleton} aria-busy="true" aria-label={t('monitor.skeleton.loading')}>
      <div className={classes.grid}>
        {/* Row 1 — Visual Site Overview + Workforce Status */}
        <CardShell span="half" tall>
          <BuildingSkeleton flexFill />
        </CardShell>
        <CardShell span="half" tall>
          <WorkforceShimmer />
        </CardShell>

        {/* Row 2 — Project Progress (full width) */}
        <CardShell span="full" short>
          <ProjectProgressShimmer />
        </CardShell>

        {/* Row 3 — Master Schedule */}
        <CardShell span="full">
          <MasterScheduleShimmer />
        </CardShell>

        {/* Row 4 — Activities + Safety + Delivery */}
        <CardShell span="third" tall>
          <ActivitiesShimmer />
        </CardShell>
        <CardShell span="third" tall>
          <SafetyShimmer />
        </CardShell>
        <CardShell span="third" tall>
          <DeliveryShimmer />
        </CardShell>

        {/* Row 5 — Weather Impact */}
        <CardShell span="full">
          <WeatherShimmer />
        </CardShell>

        {/* Row 6 — AI Agent */}
        <CardShell span="full">
          <AiAgentShimmer />
        </CardShell>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
   Card shell — matches <DashboardCard> chrome (white surface,
   border, radius, shadow) and the header line. Span + height map
   to the same grid spans the real cards use.
   ────────────────────────────────────────────────────────────────── */
function CardShell({
  span,
  tall,
  short,
  children,
}: {
  span: 'full' | 'half' | 'third';
  tall?: boolean;
  short?: boolean;
  children: React.ReactNode;
}) {
  const cls = [
    classes.card,
    span === 'full'  ? classes.spanFull  :
    span === 'third' ? classes.spanThird :
    classes.spanHalf,
    tall  ? classes.heightTall  : '',
    short ? classes.heightShort : '',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <section className={cls}>
      <header className={classes.cardHeader}>
        <span className={`${classes.shimmer} ${classes.headerTitle}`} />
        <span className={`${classes.shimmer} ${classes.headerExtra}`} />
      </header>
      <div className={classes.cardBody}>{children}</div>
    </section>
  );
}

/* ──────────────────────────────────────────────────────────────────
   Per-card body shimmers
   ────────────────────────────────────────────────────────────────── */

/* Workforce Status — 3-stat top row + trade-bar list. Real sites
   carry 10–20 active professions, so render a longer list to fill
   the locked card height (the live card's `.tradeList` already
   scrolls on overflow). */
const TRADE_WIDTHS = [
  '78%', '64%', '88%', '52%', '70%', '60%',
  '82%', '46%', '72%', '58%', '90%', '54%',
];

function WorkforceShimmer() {
  return (
    <div className={classes.workforce}>
      <div className={classes.statRow}>
        {[0, 1, 2].map((i) => (
          <div key={i} className={classes.statTile}>
            <span className={`${classes.shimmer} ${classes.statIcon}`} />
            <span className={`${classes.shimmer} ${classes.statValue}`} />
            <span className={`${classes.shimmer} ${classes.statLabel}`} />
          </div>
        ))}
      </div>
      <div className={classes.tradeList}>
        {TRADE_WIDTHS.map((w, i) => (
          <div key={i} className={classes.tradeRow}>
            <span className={`${classes.shimmer} ${classes.tradeName}`} />
            <span className={`${classes.shimmer} ${classes.tradeBar}`} style={{ width: w }} />
            <span className={`${classes.shimmer} ${classes.tradeRatio}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

/* Project Progress — ring chart + phase bars.
   Exported so the live ProjectProgressCard can reuse it as its own loading
   state (its schedule query resolves after the dashboard skeleton is gone),
   matching the WeatherShimmer / SafetyShimmer pattern. */
// Six rows = the live card's max (5 main activities + an "Other" row). The
// progress track is a fixed-width column (matching the live card, where only
// the fill varies, so the shimmer tracks are uniform); the varied widths apply
// to the name labels instead, for a natural list.
const PHASE_NAME_WIDTHS = ['42%', '30%', '46%', '34%', '38%', '28%'];

export function ProjectProgressShimmer() {
  return (
    <div className={classes.progress}>
      <div className={classes.progressHeadline}>
        <span className={`${classes.shimmer} ${classes.ringSmall}`} />
        <div className={classes.progressMeta}>
          <span className={`${classes.shimmer} ${classes.progressMetaTitle}`} />
          <span className={`${classes.shimmer} ${classes.progressMetaSub}`} />
        </div>
      </div>
      <ul className={classes.phases}>
        {PHASE_NAME_WIDTHS.map((w, i) => (
          <li key={i} className={classes.phaseRow}>
            <span className={`${classes.shimmer} ${classes.phaseIcon}`} />
            <span className={`${classes.shimmer} ${classes.phaseName}`} style={{ width: w }} />
            <span className={`${classes.shimmer} ${classes.phaseBar}`} />
            <span className={`${classes.shimmer} ${classes.phasePct}`} />
          </li>
        ))}
      </ul>
    </div>
  );
}

/* Master Schedule — Gantt rows.
   Exported so the live MasterScheduleCard can reuse it as its own loading
   state (its schedule query resolves after the dashboard skeleton is gone),
   matching the ProjectProgressShimmer / WeatherShimmer pattern. */
const GANTT_BAR_PATTERN = [
  { left: '20%', width: '15%' },
  { left: '25%', width: '25%' },
  { left: '50%', width: '30%' },
  { left: '15%', width: '40%' },
  { left: '40%', width: '50%' },
  { left: '55%', width: '20%' },
];

export function MasterScheduleShimmer() {
  return (
    <div className={classes.schedule}>
      <div className={classes.scheduleMonths}>
        {Array.from({ length: 12 }).map((_, i) => (
          <span key={i} className={`${classes.shimmer} ${classes.monthCell}`} />
        ))}
      </div>
      {GANTT_BAR_PATTERN.map((b, i) => (
        <div key={i} className={classes.scheduleRow}>
          <span className={`${classes.shimmer} ${classes.scheduleLabel}`} />
          <span className={`${classes.shimmer} ${classes.schedulePct}`} />
          <div className={classes.scheduleTrack}>
            <span
              className={`${classes.shimmer} ${classes.scheduleBar}`}
              style={{ left: b.left, width: b.width }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

/* Activities Feed — icon + title + subtitle + meta + pill. */
const FEED_TITLE_WIDTHS = ['78%', '64%', '82%', '60%', '70%', '56%'];

function ActivitiesShimmer() {
  return (
    <ul className={classes.feed}>
      {FEED_TITLE_WIDTHS.map((w, i) => (
        <li key={i} className={classes.feedItem}>
          <span className={`${classes.shimmer} ${classes.feedIcon}`} />
          <div className={classes.feedBody}>
            <div className={classes.feedTopRow}>
              <span className={`${classes.shimmer} ${classes.feedTitle}`} style={{ width: w }} />
              <span className={`${classes.shimmer} ${classes.feedPill}`} />
            </div>
            <span className={`${classes.shimmer} ${classes.feedMeta}`} style={{ width: '54%' }} />
          </div>
        </li>
      ))}
    </ul>
  );
}

/* Safety & Compliance — open-issues headline + 2 KPI tiles + 3 list rows.
   Exported so the live SafetyComplianceCard can reuse it as its own loading
   state (its query resolves after the dashboard skeleton is gone), matching
   the WeatherShimmer pattern below. */
export function SafetyShimmer() {
  return (
    <div className={classes.safety}>
      <div className={classes.safetyHeadline}>
        <span className={`${classes.shimmer} ${classes.safetyValue}`} />
        <span className={`${classes.shimmer} ${classes.safetyLabel}`} />
      </div>
      <div className={classes.safetyTiles}>
        {[0, 1].map((i) => (
          <div key={i} className={classes.safetyTile}>
            <span className={`${classes.shimmer} ${classes.safetyTileLabel}`} />
            <span className={`${classes.shimmer} ${classes.safetyTileValue}`} />
          </div>
        ))}
      </div>
      <ul className={classes.safetyFooter}>
        {[0, 1, 2].map((i) => (
          <li key={i} className={classes.safetyFooterRow}>
            <span className={`${classes.shimmer} ${classes.safetyFooterIcon}`} />
            <span
              className={`${classes.shimmer} ${classes.safetyFooterText}`}
              style={{ width: i === 0 ? '78%' : i === 1 ? '64%' : '70%' }}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}

/* Delivery — 4 incoming rows + inventory levels (4 bars) + approve button. */
function DeliveryShimmer() {
  return (
    <div className={classes.delivery}>
      <ul className={classes.deliveryRows}>
        {[0, 1, 2, 3].map((i) => (
          <li key={i} className={classes.deliveryRow}>
            <span className={`${classes.shimmer} ${classes.deliveryIcon}`} />
            <div className={classes.deliveryText}>
              <span className={`${classes.shimmer} ${classes.deliveryName}`} />
              <span className={`${classes.shimmer} ${classes.deliveryMeta}`} />
            </div>
            <span className={`${classes.shimmer} ${classes.deliveryPill}`} />
          </li>
        ))}
      </ul>
      <div className={classes.inventory}>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className={classes.inventoryRow}>
            <span className={`${classes.shimmer} ${classes.inventoryName}`} />
            <span className={`${classes.shimmer} ${classes.inventoryBar}`} />
          </div>
        ))}
      </div>
      <span className={`${classes.shimmer} ${classes.deliveryButton}`} />
    </div>
  );
}

/* WeatherAssessmentShimmer — just the activity-assessment grid (the body's left
   column), without the safety/equipment side panels. Exported so
   WeatherImpactCard can shimmer only the activities column while keeping the
   still-valid safety/equipment panels static — e.g. during the background
   refetch after reconnecting Genba. */
export function WeatherAssessmentShimmer() {
  return (
    <div className={classes.weatherAssessment}>
      <span className={`${classes.shimmer} ${classes.weatherSectionTitle}`} />
      <div className={classes.weatherTiles}>
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} className={classes.weatherTile}>
            <div className={classes.weatherTileHead}>
              <span className={`${classes.shimmer} ${classes.weatherTileName}`} />
              <span className={`${classes.shimmer} ${classes.weatherTileRisk}`} />
            </div>
            <span className={`${classes.shimmer} ${classes.weatherTileDetail}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

/* WeatherBodyShimmer — the section BELOW the forecast ribbon (activity
   assessment grid + safety/equipment side panels). Exported so
   WeatherImpactCard can show it in isolation while a newly-selected day
   loads, leaving the ribbon static and clickable above. */
export function WeatherBodyShimmer() {
  return (
    <div className={classes.weather}>
      <WeatherAssessmentShimmer />
      <aside className={classes.weatherSide}>
        {[0, 1].map((panel) => (
          <div key={panel} className={classes.weatherSidePanel}>
            <span className={`${classes.shimmer} ${classes.weatherSideTitle}`} />
            {[0, 1].map((i) => (
              <span
                key={i}
                className={`${classes.shimmer} ${classes.weatherSideRow}`}
                style={{ width: i === 0 ? '90%' : '72%' }}
              />
            ))}
          </div>
        ))}
      </aside>
    </div>
  );
}

/* WeatherShimmer — full-card shimmer (ribbon + body). Shown on first load
   before any weather data has arrived. Exported so WeatherImpactCard can
   use it as its own initial loading state. */
export function WeatherShimmer() {
  return (
    <div className={classes.weatherWrap}>
      <div className={classes.weatherRibbon}>
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} className={classes.weatherRibbonCell}>
            <span className={`${classes.shimmer} ${classes.weatherRibbonDay}`} />
            <span className={`${classes.shimmer} ${classes.weatherRibbonIcon}`} />
            <span className={`${classes.shimmer} ${classes.weatherRibbonTemp}`} />
            <span className={`${classes.shimmer} ${classes.weatherRibbonCondition}`} />
          </div>
        ))}
      </div>
      <WeatherBodyShimmer />
    </div>
  );
}

/* AI Agent — input strip + 4 prompt chips. */
const CHIP_WIDTHS = ['160px', '180px', '140px', '200px'];

function AiAgentShimmer() {
  return (
    <div className={classes.ai}>
      <span className={`${classes.shimmer} ${classes.aiInput}`} />
      <div className={classes.aiChips}>
        {CHIP_WIDTHS.map((w, i) => (
          <span key={i} className={`${classes.shimmer} ${classes.aiChip}`} style={{ width: w }} />
        ))}
      </div>
    </div>
  );
}
